import { clsx } from 'clsx';
import Image from 'next/image';

import { SharedLink } from '@monoimin/atoms';

import {
  formatPropertyCost,
  formatPropertyLocation,
} from '@monoimin/utils/formatters';
import { PROPERTY_PLACEHOLDER_IMAGE } from '@monoimin/constants';
import { getLocalePrefix } from '@monoimin/intl';

import PropertyDetails from '@monoimin/organisms/content-carousel/components/property-card/PropertyDetails';

import { isStrapiImage } from '@monoimin/utils/images';

import { pickMainImageFromEstate, createEstateUrlPath } from '../utils';

import { IconPoint } from './IconPoint';

import type { RealEstateCardVariant } from '../types';

import type {
  Locales,
  StrapiEstate,
  StrapiEstateCurrency,
} from '@monoimin/sb-types';

type RealEstateCardProps = {
  variant: RealEstateCardVariant;
  locale: Locales;
  data: StrapiEstate;
  hideDetails?: boolean;
  isPassportivity?: boolean;
};

export const RealEstateCard = ({
  variant,
  locale,
  data,
  hideDetails,
  isPassportivity,
}: RealEstateCardProps) => {
  const {
    title,
    price,
    currency,
    total_area,
    country,
    city,
    bedrooms_number,
    bathrooms_number,
    slug,
  } = data;

  const isRowVariant = variant === 'row';

  const localePrefix = getLocalePrefix(locale);

  const priceString = formatPropertyCost({
    locale: locale,
    costTo: price?.to,
    costFrom: price?.from,
    currency: currency.toUpperCase() as StrapiEstateCurrency,
  });

  const location = formatPropertyLocation({
    countryCode: country?.code,
    locale: locale as Locales,
    cityName: city?.title,
  });

  const mainImage = pickMainImageFromEstate(data);
  const shouldUnoptimize = mainImage?.src
    ? isStrapiImage(mainImage.src)
    : false;

  return (
    <div
      data-clickable='true'
      className={clsx(
        'real-estate-card relative button w-full flex flex-col gap-4 group',
        isRowVariant && 'lg:flex-row'
      )}
    >
      <div
        className={clsx('aspect-[16/10] relative overflow-hidden', {
          'w-full': !isRowVariant,
          'lg:min-w-[288px]': isRowVariant,
        })}
      >
        <Image
          src={mainImage.src || PROPERTY_PLACEHOLDER_IMAGE.src}
          fill
          alt={mainImage.alt || PROPERTY_PLACEHOLDER_IMAGE.alt}
          sizes='(max-width: 768px) 80vw, 300px'
          className='object-cover lg:group-hover:scale-105 duration-200 ease-in-out skeleton'
          unoptimized={shouldUnoptimize}
        />
      </div>
      <div
        className={clsx(
          'flex flex-col w-full',
          isRowVariant && 'lg:py-2 lg:pr-3'
        )}
      >
        {location && (
          <IconPoint
            icon='location'
            withoutPaddings
            title={location}
            className={clsx('mb-1', isRowVariant && 'lg:hidden')}
          />
        )}
        {priceString && (
          <span
            className={clsx(
              'real-estate-card__price h4 mb-4',
              isRowVariant && 'lg:mb-1'
            )}
          >
            {priceString}
          </span>
        )}
        <SharedLink
          href={createEstateUrlPath(
            localePrefix,
            country?.slug,
            slug,
            isPassportivity
          )}
          underline='off'
          className='real-estate-card__title body mb-5 leading-6'
          fullSizeOverlay
        >
          {title}
        </SharedLink>
        <div className='flex gap-5 lg:gap-4 lg:mt-auto flex-wrap justify-between w-full'>
          {Boolean(!hideDetails) && (
            <PropertyDetails
              totalArea={{
                from: total_area?.from,
                to: total_area?.to,
                locale: locale,
              }}
              bedroomCount={bedrooms_number}
              bathroomCount={bathrooms_number}
              variant='card'
              classNames={{
                container: 'text-base',
              }}
            />
          )}
          {isRowVariant && location && (
            <IconPoint
              icon='location'
              title={location}
              withoutPaddings
              className='hidden lg:flex'
            />
          )}
        </div>
      </div>
    </div>
  );
};
